
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, CircularProgress, Divider, Alert, Box, Button, ButtonGroup } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import { SET_CAMERA_SOURCE } from 'store/actions';

const EvidenceList = ({ date, device }) => {
  const dispatch = useDispatch();
  const [thumbnails, setThumbnails] = useState({});
  const [filteredVideoData, setFilteredVideoData] = useState([]);
  const [allVideoData, setAllVideoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('');
  const [visibleItems, setVisibleItems] = useState(5);
  const [videoType, setVideoType] = useState('dock');

  const containerRef = useRef(null);
  const observerRef = useRef(null);
  const processingThumbnails = useRef(new Set());
  const generatedThumbnails = useRef(new Set());
  const videoRefs = useRef({});
  const loadingRef = useRef(false);
  const currentDate = useSelector((state) => state.customization.currentDate);
  const selectedCamera = useSelector((state) => state.cameraReducer.cameraData);

  // Check if current device is D03
  const isD03Device = () => {
    const currentDevice = device?.device_name || selectedCamera?.device_name;
    return currentDevice === 'D03';
  };

  // Filter videos by date
  const filterVideosByDate = useCallback((videos, selectedDate) => {
    if (!videos || !videos.length || !selectedDate) return [];
    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 59, 59, 999);
    return videos.filter((video) => {
      const videoDate = new Date(video.start_time);
      return videoDate >= startOfDay && videoDate <= endOfDay;
    });
  }, []);

  // Updated filter function based on API response
  const filterVideosByType = useCallback((videos, type) => {
    if (!videos || !videos.length) return [];

    // Only filter by type if it's D03 device, otherwise return all videos
    if (isD03Device()) {
      if (type === 'dock') {
        return videos.filter((video) => video.type === 'dock');
      } else if (type === 'safety') {
        return videos.filter((video) => video.type === 'safety');
      }
    }
    return videos;
  }, [device, selectedCamera]);

  // Fetch video data
  const fetchVideoData = async (date, device) => {
    try {
      setLoading(true);
      setError(null);
      setThumbnails({});
      processingThumbnails.current.clear();
      generatedThumbnails.current.clear();

      const response = await fetch('https://neoe2e.neophyte.live/fm-api/kpiCreation/evidenceList', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, device })
      });

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      const filteredByDate = filterVideosByDate(data, date || currentDate);

      setAllVideoData(filteredByDate);
      
      // Only apply type filtering for D03 device
      const filteredByType = isD03Device() ? filterVideosByType(filteredByDate, videoType) : filteredByDate;
      setFilteredVideoData(filteredByType);

      if (filteredByType?.[0]?.videos) {
        const url = `${filteredByType[0].videos}`;
        setSource(url);
        dispatch({ type: SET_CAMERA_SOURCE, videoSource: url });
      }
    } catch (error) {
      console.error('Error fetching video data:', error);
      setError(error.message || 'Failed to fetch evidence data');
    } finally {
      setLoading(false);
    }
  };

  // Handle video type change
  const handleVideoTypeChange = (type) => {
    setVideoType(type);
    setVisibleItems(5);
    const filtered = filterVideosByType(allVideoData, type);
    setFilteredVideoData(filtered);

    if (filtered?.[0]?.videos) {
      const url = `${filtered[0].videos}`;
      setSource(url);
      dispatch({ type: SET_CAMERA_SOURCE, videoSource: url });
    } else {
      setSource('');
    }
  };

  // Generate thumbnail for a specific video
  const generateThumbnail = useCallback(
    (videoPath, index) => {
      if (generatedThumbnails.current.has(index) || processingThumbnails.current.has(index) || thumbnails[index]) {
        return;
      }

      processingThumbnails.current.add(index);

      const video = document.createElement('video');
      videoRefs.current[index] = video;
      video.src = `${videoPath}`;
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';
      video.muted = true;

      const cleanup = () => {
        processingThumbnails.current.delete(index);
        generatedThumbnails.current.add(index);
        if (video.timeoutId) clearTimeout(video.timeoutId);
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onError);
        video.src = '';
        delete videoRefs.current[index];
      };

      const onLoadedMetadata = () => {
        video.currentTime = Math.min(2, video.duration * 0.1);
      };

      const onSeeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 240;
          canvas.height = 135;
          const ctx = canvas.getContext('2d');

          ctx.fillStyle = '#000';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);

          setThumbnails((prev) => ({ ...prev, [index]: thumbnailUrl }));
        } catch (e) {
          console.error(`Thumbnail generation error for index ${index}:`, e);
          setThumbnails((prev) => ({ ...prev, [index]: null }));
        } finally {
          cleanup();
        }
      };

      const onError = (e) => {
        console.error(`Video load error for index ${index}:`, e);
        setThumbnails((prev) => ({ ...prev, [index]: null }));
        cleanup();
      };

      video.addEventListener('loadedmetadata', onLoadedMetadata);
      video.addEventListener('seeked', onSeeked);
      video.addEventListener('error', onError);

      video.timeoutId = setTimeout(() => {
        console.warn(`Thumbnail timeout for index ${index}`);
        setThumbnails((prev) => ({ ...prev, [index]: null }));
        cleanup();
      }, 15000);
    },
    [thumbnails]
  );

  // Setup intersection observer for lazy loading thumbnails
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!filteredVideoData?.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index, 10);
            const videoPath = filteredVideoData[index]?.videos;

            if (videoPath && !generatedThumbnails.current.has(index) && !processingThumbnails.current.has(index) && !thumbnails[index]) {
              generateThumbnail(videoPath, index);
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '200px'
      }
    );

    observerRef.current = observer;

    setTimeout(() => {
      const items = document.querySelectorAll('[data-video-item]');
      items.forEach((item) => {
        if (observerRef.current) {
          observerRef.current.observe(item);
        }
      });
    }, 100);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [filteredVideoData, generateThumbnail, visibleItems]);

  // Load more items on scroll
  const loadMoreItems = useCallback(() => {
    if (loadingRef.current || visibleItems >= filteredVideoData.length) return;

    loadingRef.current = true;
    setVisibleItems((prev) => {
      const newValue = Math.min(prev + 5, filteredVideoData.length);
      setTimeout(() => {
        loadingRef.current = false;
      }, 100);
      return newValue;
    });
  }, [visibleItems, filteredVideoData.length]);

  // Scroll event listener with throttling
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (window.innerHeight + window.scrollY > document.body.offsetHeight - 800) {
            loadMoreItems();
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreItems]);

  // Cleanup on unmount or data change
  useEffect(() => {
    return () => {
      processingThumbnails.current.clear();
      generatedThumbnails.current.clear();

      Object.values(videoRefs.current).forEach((video) => {
        if (video) {
          if (video.timeoutId) clearTimeout(video.timeoutId);
          video.src = '';
        }
      });
      videoRefs.current = {};

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  // Fetch data when params change
  useEffect(() => {
    const dateToUse = date || currentDate;
    const deviceToUse = device?.device_name || selectedCamera?.device_name;
    if (dateToUse && deviceToUse) {
      fetchVideoData(dateToUse, deviceToUse);
    }
  }, [date, device, currentDate, selectedCamera]);

  const getTypeColor = (type) =>
    ({
      alert: 'bg-red-500',
      motion: 'bg-orange-500',
      person: 'bg-purple-500',
      vehicle: 'bg-blue-500',
      dock: 'bg-blue-500',
      unloaded: 'bg-blue-500',
      safety: 'bg-gray-500'
    }[type?.toLowerCase()] || 'bg-gray-500');

  const getSafetyItemColor = (item) =>
    ({
      shoes: 'bg-red-500',
      ppe: 'bg-red-500',
      helmet: 'bg-red-500',
      entry: 'bg-blue-500',
      exit: 'bg-blue-500'
    }[item?.toLowerCase()] || 'bg-gray-500');

  const formatDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
      : '';

  // Render states
  if (loading) {
    return (
      <Box className="flex justify-center items-center h-64">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className="my-4">
        {error}
      </Alert>
    );
  }

  return (
    <div ref={containerRef}>
      <Divider className="my-4" />
      <div className="mb-4">
        <div className="flex justify-between items-center mb-3">
          <Typography variant="h6">
            All Evidence ({filteredVideoData.length})
            {isD03Device() && ` - ${videoType.charAt(0).toUpperCase() + videoType.slice(1)}`}
          </Typography>
          
          {/* Conditionally render toggle buttons only for D03 device */}
          {isD03Device() && (
            <ButtonGroup variant="contained" size="small">
              <Button onClick={() => handleVideoTypeChange('dock')} color={videoType === 'dock' ? 'primary' : 'inherit'}>
                Dock
              </Button>
              {/* <Button onClick={() => handleVideoTypeChange('dock')} color={videoType === 'unloaded' ? 'primary' : 'inherit'}>
                Unloaded
              </Button> */}
              <Button onClick={() => handleVideoTypeChange('safety')} color={videoType === 'safety' ? 'primary' : 'inherit'}>
                Safety
              </Button>
            </ButtonGroup>
          )}
        </div>

        {!filteredVideoData?.length ? (
          <Alert severity="info" className="my-4">
            No {isD03Device() ? videoType : ''} evidence found for this date and device.
          </Alert>
        ) : (
          <>
            <div className="mb-0 border flex flex-col space-y-4">
              {filteredVideoData.slice(0, visibleItems).map((videoGroup, index) => (
                <div key={`video-${index}-${videoGroup.start_time}`} data-video-item data-index={index}>
                  <div
                    className={`flex border bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden ${
                      source.endsWith(videoGroup?.videos) ? 'ring-2 ring-blue-500' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      if (videoGroup?.videos) {
                        const url = `${videoGroup.videos}`;
                        setSource(url);
                        dispatch({ type: SET_CAMERA_SOURCE, videoSource: url });
                      }
                    }}
                  >
                    <div className="w-1/2">
                      <div className="relative w-full h-full aspect-video object-cover bg-gray-200 overflow-hidden">
                        {thumbnails[index] ? (
                          <img
                            src={thumbnails[index]}
                            alt={`${videoGroup?.type || 'Video'} Thumbnail`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            {processingThumbnails.current.has(index) ? <CircularProgress size={24} /> : <VideocamIcon />}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="w-1/2 p-2 flex flex-col justify-center">
                      <div className="flex flex-wrap gap-2">
                        <div className={`${getTypeColor(videoGroup?.type)} text-white text-sm px-3 py-1.5 rounded-full w-fit font-medium`}>
                          {videoGroup?.type?.charAt(0)?.toUpperCase() + videoGroup?.type?.slice(1) || 'Video'}
                        </div>


                        {videoGroup.type === 'dock' && (
                          <>
                            {videoGroup?.instance && (
                              <div className={`${getSafetyItemColor('unloaded')} text-white text-sm px-3 py-1.5 rounded-full w-fit font-medium`}>
                                <span>Unloaded</span>
                              </div>
                            )}
                            </>
                        )}

                        {videoGroup.type === 'safety' && (
                          <>
                            {videoGroup.ppe === true && (
                              <div className={`${getSafetyItemColor('ppe')} text-white text-sm px-3 py-1.5 rounded-full w-fit font-medium`}>
                                <span>PPE</span>
                              </div>
                            )}
                            {videoGroup.shoes === true && (
                              <div
                                className={`${getSafetyItemColor('shoes')} text-white text-sm px-3 py-1.5 rounded-full w-fit font-medium`}
                              >
                                <span>Shoes</span>
                              </div>
                            )}
                             {videoGroup.helmet === true && (
                              <div
                                className={`${getSafetyItemColor('helmet')} text-white text-sm px-3 py-1.5 rounded-full w-fit font-medium`}
                              >
                                <span>Helmet</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      <div className="space-y-2 mt-3">
                        <div className="flex items-start">
                          <span className="text-gray-500 text-sm w-16">Start:</span>
                          <span className="text-gray-800 font-medium text-sm">{formatDate(videoGroup.start_time)}</span>
                        </div>
                        <div className="flex items-start">
                          <span className="text-gray-500 text-sm w-16">End:</span>
                          <span className="text-gray-800 font-medium text-sm">{formatDate(videoGroup.end_time)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {visibleItems < filteredVideoData.length && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={loadMoreItems}
                  disabled={loadingRef.current}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  {loadingRef.current ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EvidenceList;